import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { motion } from 'framer-motion';
import packageJson from '../../../../../package.json';
import { theme } from '../../../../theme';

const MetaRow = ({ label, value, isLink, isVersion, index }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || document.documentElement.dir === 'rtl';
  if (value == null || value === '') return null;
  const displayValue = String(value).trim();
  if (!displayValue) return null;
  const versionTag = isVersion && displayValue !== '—' ? `v${displayValue}` : displayValue;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="group py-4 px-4 rounded-xl hover:bg-gray-50/50 transition-all duration-200"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: theme.fonts.body }}>
          {label}
        </span>
        {isLink ? (
          <a
            href={displayValue}
            target="_blank"
            rel="noopener noreferrer"
            className="text-base font-semibold text-[#0ea5e9] hover:text-[#0284c7] transition-colors inline-flex items-center gap-1 group-hover:gap-2 break-all"
            style={{ fontFamily: theme.fonts.body }}
          >
            {t('versionDetails.viewRepository')}
            <span className="transition-transform group-hover:translate-x-1" dir="ltr">{isRTL ? '←' : '→'}</span>
          </a>
        ) : isVersion && displayValue !== '—' ? (
          <span
            className="inline-flex items-center px-5 py-2.5 rounded-full text-base font-bold tracking-tight bg-gradient-to-r from-[#0ea5e9] to-[#d946ef] text-white shadow-[0_4px_14px_0_rgba(14,165,233,0.4)] hover:shadow-[0_6px_20px_0_rgba(14,165,233,0.5)] border border-white/30 transition-all duration-300 hover:scale-105"
            style={{ fontFamily: theme.fonts.body }}
          >
            {versionTag}
          </span>
        ) : (
          <p className="text-base font-semibold text-gray-900" style={{ fontFamily: theme.fonts.body }}>
            {displayValue}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default function VersionDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const version = packageJson.version || '';
  const productName = packageJson.productName || packageJson.name || 'Aptiview Platform';
  const description = packageJson.description || '';
  const license = packageJson.license || '';
  const author = packageJson.author || '';
  const company = packageJson.company || '';
  const rawRepo = (packageJson.repository && typeof packageJson.repository === 'object' && packageJson.repository.url) || (typeof packageJson.repository === 'string' ? packageJson.repository : '') || '';
  const repoUrl = rawRepo ? String(rawRepo).replace(/^git\+/, '').trim() : '';

  return (
    <div className="min-h-screen bg-gray-50 font-body" style={{ fontFamily: theme.fonts.body, paddingTop: '10px' }}>
      <div className="max-w-8xl mx-auto p-2 sm:p-4">
        {/* Header - same width as content below */}
        {/* Header */}
        <div className="mb-4">
          <div
            className="w-full px-4 py-3 rounded-xl text-white"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white flex-shrink-0"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-semibold mb-0.5 text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t('versionDetails.title')}
                </h1>
                <p className="text-xs text-blue-100" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t('versionDetails.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Metadata card - same width as header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="mt-6 relative rounded-3xl border border-gray-200/60 bg-gradient-to-br from-white/80 to-gray-50/60 backdrop-blur-md shadow-[0_8px_35px_-8px_rgba(0,0,0,0.12)] transition-all duration-300 hover:shadow-[0_12px_45px_-6px_rgba(0,0,0,0.16)] px-6 py-10 sm:px-8 sm:py-12"
        >
          <div className="space-y-2">
            <MetaRow label={t('versionDetails.appName')} value={productName} index={0} />
            <MetaRow label={t('versionDetails.version')} value={version} index={1} isVersion />
            <MetaRow label={t('versionDetails.description')} value={description} index={2} />
            <MetaRow label={t('versionDetails.license')} value={license} index={3} />
            <MetaRow label={t('versionDetails.author')} value={author} index={4} />
            <MetaRow label={t('versionDetails.company')} value={company} index={5} />
            <MetaRow label={t('versionDetails.repository')} value={repoUrl} isLink index={6} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
